import { defaultErrorHandler } from 'api';
import { ApplicationSegment } from 'components/ApplicationSegment';
import { EmptyStateMessage } from 'components/EmptyStateMessage';
import { PageHeader } from 'components/Layout/PageHeader';
import { PageLayout } from 'components/Layout/PageLayout';
import { usePeriodDates } from 'hooks/usePeriodDates';
import { Entry } from 'modules/entry/types';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { DashboardApi } from '../api';
import { DashboardView } from '../components/DashboardView/DashboardView';
import { DashboardTab, Tabs } from '../components/DashboardView/Tabs';
import {
  DashboardStats,
  SubsetDetailedStats,
  useDashboardStats,
} from '../hooks/useDashboardStats';

const m = defineMessages({
  pageTitle: {
    id: 'Dashboard.Page.Title',
    defaultMessage: 'Tableau de bord',
  },
  noEntry: {
    id: 'Dashboard.Page.NoEntryMessage',
    defaultMessage: 'Aucune entrée',
  },
});

export const DashboardPage: React.FC = () => {
  const { formatMessage } = useIntl();
  const [stats, setStats] = useState<DashboardStats>();
  const [filteredStats, setFileteredStats] = useState<SubsetDetailedStats>();
  const [entries, setEntries] = useState<Entry[]>([]);

  const {
    periodName,
    customPeriodDates,
    startDate,
    endDate,
    setPeriodName,
    setCustomPeriodDates,
  } = usePeriodDates();

  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTab, setCurrentTab] = useState<DashboardTab>(
    DashboardTab.MyEntries
  );

  const { getDashboardStats, isReady: useDashboardStatsReady } =
    useDashboardStats();

  useEffect(() => {
    if (!stats) {
      setFileteredStats(undefined);
      return;
    }

    switch (currentTab) {
      case DashboardTab.AllEntries:
        setFileteredStats(stats.allEntries);
        break;
      case DashboardTab.MyTeamEntries:
        setFileteredStats(stats.myTeamEntriesStats);
        break;
      default:
        setFileteredStats(stats.myEntriesStats);
    }
  }, [stats, currentTab]);

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    DashboardApi.get({
      fromDate: startDate.toISOString(),
      toDate: endDate.toISOString(),
    })
      .then((fetchedEntries) => {
        setEntries(fetchedEntries);
      })
      .catch((error) => {
        defaultErrorHandler(error);
      })
      .finally(() => setIsLoaded(true));
  }, [startDate, endDate]);

  useEffect(() => {
    if (!entries.length || !useDashboardStatsReady) {
      setStats(undefined);
      return;
    }

    setStats(getDashboardStats(entries));
  }, [entries, getDashboardStats, useDashboardStatsReady]);

  return (
    <PageLayout pageTitle={formatMessage(m.pageTitle)}>
      <PageHeader header={formatMessage(m.pageTitle)} iconName="th large" />

      <Tabs
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        periodName={periodName}
        onPeriodChange={setPeriodName}
        setPeriodDates={setCustomPeriodDates}
        periodDates={customPeriodDates}
      />

      {filteredStats && <DashboardView stats={filteredStats} />}

      {isLoaded && !filteredStats && (
        <ApplicationSegment>
          <EmptyStateMessage message={formatMessage(m.noEntry)} />
        </ApplicationSegment>
      )}
    </PageLayout>
  );
};
